import Icon from 'view/Icon';
import Product, * as Prod from 'data/Product';
import React, { Suspense, useMemo } from 'react';
import styled from 'styled-components';
import Variant from 'data/Variant';
import { Color, FontWeight, Size } from 'lib/styles';
import { Background, Border, Main } from 'styles/themeColors';
import { fieldName, getBasicColumns, getColumnMetaData, getRentalColumnsFn, getTypeColumns, countriesIncludeRegion, getCommissionColumnKeys, buildCommissionColumnGroups } from 'utils/variant';
import FeatureFlag from 'data/FeatureFlag';
import Country from 'data/Country';
import Currency from 'data/Currency';
import { useAuth } from 'lib/Auth';
import Vendor from 'data/Vendor';
import SortingArrow from './SortingArrow';
import Checkbox from 'components/UI/Checkbox';
import { Sorting } from 'common/ProductDetailsV2';
import { Row } from 'common/VariantTableRowV2';
import LoadingIndicator from 'components/UI/LoadingIndicator';
import { TEXT_LABELS } from 'constants/productDetails';
import { getColumnMetaDataForWarehouse, getFieldNameForWarehouseColumn } from 'utils/warehouse';
import { COUNTRY_ISO } from 'constants/country';
import { PRICE_TYPE, PRODUCT_TYPE } from 'constants/product';
import { getProductTypeConfig } from 'utils/product';
import { ReadOnlyWrapper } from 'components/common/ReadOnlyWrapper';
import { useReadPermission } from 'hooks/useUserRoles';
import { PRODUCT_STATUS } from 'constants/product';
const VariantTableRowV2 = React.lazy(() => import('common/VariantTableRowV2'));

const BaseTable = styled.table`
  border-spacing: 0;
  color: ${Main.accent};
  text-align: left;
  width: 100%;
  border: none !important;
  border-radius: 10px;
`;

const Container = styled.div<{ maxHeight?: string }>`
  -webkit-overflow-scrolling: touch;
  margin-left: -2.25rem;
  margin-top: ${Size.large};
  overflow-x: auto;
  display: flex;
  overflow-y: auto;
  max-height: ${({ maxHeight }) => maxHeight ? `${maxHeight}` : '70vh'};
`;

const Th = styled.th<any>`
  background-color: ${Main.white};
  padding: 10px;
  position: relative;
  white-space: nowrap;
  left: 0;
  border-top: 1px solid ${Border.light};
  // border-left: 1px solid ${Color.lightGray};
  // border-right: 1px solid ${Color.lightGray};
  color: ${Color.black};
`;

const ThParent = styled.th<any>`
  background-color: ${Background.light};
  color: ${Color.black};
  padding: 10px;
  position: relative;
  white-space: nowrap;
  left: 0;
  // border-left: 1px solid ${Color.lightGray};
  // border-right: 1px solid ${Color.lightGray};
  font-weight: 100;
`;

const IconContainer = styled.div`
  display: inline-flex;
  flex-direction: column;
  cursor: pointer;
  margin-right: 5px;
  vertical-align: top;
`;

const ThAction = styled.th`
  font-size: 0;
`;

const ThContentsParent = styled.div`
  align-items: center;
  display: flex;
  justify-content: center;
`;

const ThContents = styled.div`
  align-items: center;
  display: flex;
  justify-content: center;
  font-weight: ${FontWeight.bold};
  width: 100%;
`;

const Headers = styled.tr`
  th:last-parent {
    border-right: 1px solid ${Border.light};
    border-top-right-radius: 10px;
  }
  th:last-child {
    border-right: 1px solid ${Border.light};
  }
`;

const Thead = styled.thead`
  position: sticky;
  top: 0;
  z-index: 99;
  background-color: #ffffff;
`;

const ButtonIcon = styled(Icon).attrs({ size: 24 }) <any>`
  color: #666c73;
`;

const KEYS_FOR_SORT = [
  'checkbox'
];

export interface Column {
  key: string;
  label: string;
  keyId?: number;
  isWarehouseColumn?: boolean;
  isUnitPriceColumn?: boolean;
  name?: string;
  id?: number;
  isRentalPriceColumn?: boolean,
}

export interface Props {
  classifications: Prod.ProductAttribute[];
  focusedMode: boolean;
  isErrorColumn: (key: string, sku: string) => boolean;
  onAttributeEdit: (key: number, headerKey: string) => void;
  onCellEdit: (
    newAttributeValue: string | number | boolean,
    variant: Variant,
    attributeKey: string,
    keyId?: number
  ) => void;
  onRowDelete: () => void;
  rows: Row[];
  attributes: Prod.ProductAttribute[];
  suggestionValuesList: string[];
  clearSuggestions: () => void;
  originalId?: number;
  product: Product;
  disabled?: boolean;
  publishedSKUList: string[];
  featureFlags: FeatureFlag;
  currencies: Currency[];
  selectedVendor: Vendor;
  stockFlag?: boolean;
  selectedVariants: number[];
  setSortByHeader: (value: Sorting) => void;
  sortByHeader: Sorting | undefined;
  selectUnselectAllVariants: (value: boolean) => void;
  allVariantsSelection: boolean;
  setAllVariantsSelection: (value: boolean) => void;
  type?: string;
  filteredWarehouseIds: string[];
  columnSettings: any[];
  smartVariantMode?: boolean;
  onBlur?: (key: string, value: string) => string | null;
  maxHeight?: string;
  commissionReadOnly?: boolean;
}

export default function TableV2({
  classifications,
  focusedMode,
  isErrorColumn,
  onAttributeEdit,
  onCellEdit,
  onRowDelete,
  rows,
  suggestionValuesList,
  attributes,
  clearSuggestions,
  originalId,
  product,
  disabled,
  publishedSKUList,
  currencies,
  selectedVendor,
  stockFlag,
  featureFlags,
  selectedVariants,
  setSortByHeader,
  sortByHeader,
  selectUnselectAllVariants,
  allVariantsSelection,
  setAllVariantsSelection,
  type,
  filteredWarehouseIds,
  columnSettings,
  smartVariantMode,
  onBlur,
  maxHeight,
  commissionReadOnly,
}: Props) {
  const { user } = useAuth();

  const typeConfig = getProductTypeConfig(product.productType);
  const { readOnly: baseDeleteReadOnly } = useReadPermission(typeConfig.permissions.variantsDelete);
  const isDraftStatus = product.status === PRODUCT_STATUS.DRAFT;
  const isDeleteReadOnly = isDraftStatus && baseDeleteReadOnly;
  // Memoize expensive productStocks calculation
  const productStocks = useMemo(() =>
    product.variants?.map((variant: Variant) => variant.productStocks).flat() || [],
    [product.variants] // Only recompute when variants array changes
  );

  // Memoize variants rental check
  const hasRentalVariants = useMemo(() =>
    product.variants.some(x => x.rent),
    [product.variants]
  );

  const productWarehouses = product.warehouses;
  const selectedWarehouses = type === TEXT_LABELS.WAREHOUSES
    ? productWarehouses.filter(warehouse => filteredWarehouseIds.includes(`${warehouse.type}-${warehouse.id}`))
    : productWarehouses;

  const warehouseCountriesPurchase = selectedWarehouses.filter(x => x.type === PRICE_TYPE.PURCHASE).map(
    (warehouse: Prod.ProductWarehouse) => warehouse.address.country
  );

  const warehouseCountriesRent = selectedWarehouses.filter(x => x.type === PRICE_TYPE.RENT).map(
    (warehouse: Prod.ProductWarehouse) => warehouse.address.country
  );

  // Filtering duplicate countries
  const availableCountriesPurchase = warehouseCountriesPurchase
    .filter(
      (country: Country, index: number, self: any) =>
        index ===
        self.findIndex((t: any) => t?.countryName === country?.countryName)
    )
    .map((country: Country) => {
      const currency = currencies.find(
        (currency: Currency) => currency.countryId === country?.id
      );
      return { ...country, currency };
    });

  const availableCountriesRent = warehouseCountriesRent
    .filter(
      (country: Country, index: number, self: any) =>
        index ===
        self.findIndex((t: any) => t?.countryName === country?.countryName)
    )
    .map((country: Country) => {
      const currency = currencies.find(
        (currency: Currency) => currency.countryId === country?.id
      );
      return { ...country, currency };
    });


  const getRangeUnitColumns = (countries: Country[]) => {
    let columns: Column[] = [];
    countries.forEach(country => {

      if (country.countryIso === COUNTRY_ISO.US) {
        columns.push(
          {
            key: `rangeUnit-${country.countryIso}`,
            label: `${TEXT_LABELS.RANGE_UNIT_US_LABEL}`,
          }
        );
      }

      if (country.countryIso === COUNTRY_ISO.CA) {
        columns.push(
          {
            key: `rangeUnit-${country.countryIso}`,
            label: `${TEXT_LABELS.RANGE_UNIT_CA_LABEL}`,
          }
        );
      }
    });

    return columns;
  }

  const getRentalPriceColumns = (countries: Country[]) => {
    let columns: Column[] = [];
    countries.forEach(country => {
      if (country.countryIso === COUNTRY_ISO.US) {
        columns.push(
          {
            key: `rentalPrice-${country.countryIso}`,
            label: `${TEXT_LABELS.RENTAL_PRICE_US_LABEL}`,
            isRentalPriceColumn: true,
          }
        );
      }

      if (country.countryIso === COUNTRY_ISO.CA) {
        columns.push(
          {
            key: `rentalPrice-${country.countryIso}`,
            label: `${TEXT_LABELS.RENTAL_PRICE_CA_LABEL}`,
            isRentalPriceColumn: true,
          }
        );
      }
    });

    return columns;
  }


  const getRentalColumns = () => {
    // const columns = getRentalColumnsFn();

    let rentalColumns: any = [];
    if (featureFlags?.rent?.enabled) {

      const rangeUnitColumns = getRangeUnitColumns(availableCountriesRent);

      if (rangeUnitColumns.length) {
        rentalColumns.push({
          key: TEXT_LABELS.RANGE_UNITS,
          name: TEXT_LABELS.UNITS_OF_MEASUREMENT,
          colSpan: rangeUnitColumns.length,
          columns: rangeUnitColumns
        });
      }

      const rentalPriceColumns = getRentalPriceColumns(availableCountriesRent);

      if (rentalPriceColumns.length) {
        rentalColumns.push({
          key: TEXT_LABELS.RENTAL_PRICE,
          name: TEXT_LABELS.RENTAL_PRICE_LABEL,
          colSpan: rentalPriceColumns.length,
          columns: rentalPriceColumns
        });
      }

      // availableCountriesRent.forEach((country) => {
      //   if (country.countryIso === COUNTRY_ISO.US || country.countryIso === COUNTRY_ISO.CA) {
      //     for (let i = 1; i <= 5; i++) {
      //       const key = `${i}-${country.countryIso}`;
      //       rentalColumns.push({
      //         key: key,
      //         name: `Rental price ${i} (${country.countryIso})`,
      //         colSpan: columns.length,
      //         columns: columns.map((col) => { return { key: `${col}-${key}`, label: fieldName(col), isRentalColumn: col !== TEXT_LABELS.RANGE_UNITS, name: col } }),
      //       });
      //     }
      //   }
      // });
    }
    return rentalColumns;
  };

  const parentHeaders = useMemo(() => {
    let basicColumns = getBasicColumns(
      product.brand?.brandVendor!,
      selectedVendor?.isSlbVendor,
      user?.role
    );
    if (product.productType === PRODUCT_TYPE.SUBSCRIPTION) {
      basicColumns = [...basicColumns, 'duration'];
    }
    const showCommissionColumns = !product.productType || product.productType === PRODUCT_TYPE.REGULAR;
    // Commission only makes sense for a region the product actually has a purchase warehouse in
    const hasUsPurchaseWarehouse = countriesIncludeRegion(availableCountriesPurchase, 'US');
    const hasCaPurchaseWarehouse = countriesIncludeRegion(availableCountriesPurchase, 'CA');

    basicColumns = basicColumns.filter(x => columnSettings.length === 0 || columnSettings.find(y => y.key === x && !y.hidden))

    let commissionColumnsUS = getCommissionColumnKeys('US', showCommissionColumns, hasUsPurchaseWarehouse);
    commissionColumnsUS = commissionColumnsUS.filter(x => columnSettings.length === 0 || columnSettings.find(y => y.key === x && !y.hidden));

    let commissionColumnsCA = getCommissionColumnKeys('CA', showCommissionColumns, hasCaPurchaseWarehouse);
    commissionColumnsCA = commissionColumnsCA.filter(x => columnSettings.length === 0 || columnSettings.find(y => y.key === x && !y.hidden));

    let typeColumns = getTypeColumns(featureFlags?.rent?.enabled);
    typeColumns = typeColumns.filter(x => columnSettings.length === 0 || columnSettings.find(y => y.key === x && !y.hidden))

    attributes = attributes.filter(x => columnSettings.length === 0 || columnSettings.find(y => y.key === x.name && !y.hidden))

    let unitPricesColumns: Column[] = availableCountriesPurchase.map((country) => ({
      label: `${country.countryIso} purchase price`,
      key: `pricePerUnit-${country.id}`,
      isUnitPriceColumn: true,
    }));

    unitPricesColumns = unitPricesColumns.filter(x => columnSettings.length === 0 || columnSettings.find(y => y.key === x.key && !y.hidden))

    let warehouseColumnsKeys = ['available', 'Inventory', 'deliveryLeadDays'];

    const warehouseColumns = selectedWarehouses.reduce((acc, warehouse) => {
      let updatedWarehouseColumnsKeys = [...warehouseColumnsKeys];

      // Remove the postOrderShippingCost key if warehouse is pickup only
      if (warehouse.pickup && !warehouse.delivery) {
        updatedWarehouseColumnsKeys = updatedWarehouseColumnsKeys.filter(key => key !== TEXT_LABELS.POST_ORDER_SHIPPING_COST);
      } else {
        // Add the postOrderShippingCost key if it is not already included and product has postOrderShippingCost
        if (!updatedWarehouseColumnsKeys.includes(TEXT_LABELS.POST_ORDER_SHIPPING_COST) && product.postOrderShippingCost) {
          updatedWarehouseColumnsKeys.push(TEXT_LABELS.POST_ORDER_SHIPPING_COST);
        }
      }

      // Remove Inventory key if stockFlag is true or Stock API is disabled
      if (stockFlag || !product.brandVendor?.isStockApiEnabled) {
        updatedWarehouseColumnsKeys = updatedWarehouseColumnsKeys.filter(key => key !== TEXT_LABELS.INVENTORY);
      }

      // Skip processing if rent feature flag is disabled and warehouse type is RENT
      if (!featureFlags?.rent?.enabled && warehouse?.type === PRICE_TYPE.RENT) {
        return acc;
      }

      acc.push({
        key: `${warehouse.name}-${warehouse.id}-${warehouse.type}`,
        name: `"${warehouse.address.countryIsoCode}-${warehouse.name}" ${TEXT_LABELS.WAREHOUSE} ${warehouse.type}`,
        colSpan: updatedWarehouseColumnsKeys.length,
        columns: updatedWarehouseColumnsKeys.map(key => {
          const label = `${getFieldNameForWarehouseColumn(key)} "${warehouse.address?.countryIsoCode}-${warehouse.name}" ${TEXT_LABELS.WAREHOUSE.toLocaleLowerCase()}`;
          return getColumnMetaDataForWarehouse(key, label, warehouse);
        })
      });

      return acc;
    }, [] as Array<{ key: string, name: string, colSpan: number, columns: any[] }>);

    const columns = type === TEXT_LABELS.WAREHOUSES ?
      [
        {
          key: 'basic',
          name: 'Basic',
          colSpan: 1,
          columns: basicColumns.filter(x => x === 'sku').map((key) => getColumnMetaData(key)),
        },
        {
          key: 'type',
          name: 'Type',
          colSpan: typeColumns.length,
          columns: typeColumns.map((key) => getColumnMetaData(key)),
        },
        {
          key: 'selectableAttributes',
          name: 'Selectable Attributes',
          colSpan: attributes.length,
          columns: attributes.map((key) => getColumnMetaData(key)),
        },
        {
          key: 'tehcnicalSpecifications',
          name: 'Technical Specifications',
          colSpan: classifications.length,
          columns: classifications.map((key) => getColumnMetaData(key)),
        },
      ]
      : [
        {
          key: 'delete',
          name: smartVariantMode ? null : (
            <ReadOnlyWrapper readOnly={isDeleteReadOnly}>
              <ButtonIcon name='DeleteVariants'
                className={selectedVariants.length && !disabled && !isDeleteReadOnly ? "" : "disabled-btn"}
                onClick={!disabled && !isDeleteReadOnly && onRowDelete} />
            </ReadOnlyWrapper>
          ),
          colSpan: 1,
          columns: [
            {
              key: 'checkbox',
              label: (
                <div style={{ display: "flex", flex: 1, justifyContent: "center", alignItems: "center" }}>
                  <Checkbox
                    id="table-editable-cell-checkbox-id"
                    checkboxSize="xs"
                    appearance="none"
                    checked={allVariantsSelection}
                    disabled={smartVariantMode ? false : disabled}
                    onChange={(value) => {
                      selectUnselectAllVariants(value);
                      setAllVariantsSelection(value);
                    }}
                    theme={allVariantsSelection ? 'brandLight' : 'lightGray'}
                    bgColor={allVariantsSelection ? Main.brandLight : Background.light}
                  />
                </div>
              ),
            },
          ],
        },
        {
          key: 'basic',
          name: 'Basic',
          colSpan: basicColumns.length,
          columns: [...basicColumns.map((key) => getColumnMetaData(key))],
        },
        ...buildCommissionColumnGroups(commissionColumnsUS, commissionColumnsCA),
        {
          key: 'type',
          name: 'Type',
          colSpan: typeColumns.length,
          columns: typeColumns.map((key) => getColumnMetaData(key)),
        },
        {
          key: 'selectableAttributes',
          name: 'Selectable Attributes',
          colSpan: attributes.length,
          columns: attributes.map((key) => getColumnMetaData(key)),
        },
        {
          key: 'tehcnicalSpecifications',
          name: 'Technical Specifications',
          colSpan: classifications.length,
          columns: classifications.map((key) => getColumnMetaData(key)),
        },
      ];

    // Use memoized hasRentalVariants instead of doing the find operation each time
    if (hasRentalVariants) {
      const rentalColumns = getRentalColumns();
      rentalColumns.forEach((rc: any) => {
        columns.push(rc);
      });
    }

    if (warehouseColumns.length && type === TEXT_LABELS.WAREHOUSES) columns.push(...warehouseColumns)


    if (unitPricesColumns.length) {
      const unitPriceColumn = {
        key: TEXT_LABELS.UNIT_PRICES,
        name: TEXT_LABELS.PURCHASE_UNIT_PRICE,
        colSpan: unitPricesColumns.length,
        columns: unitPricesColumns,
      };
      columns.push(unitPriceColumn);
    }

    return columns;
  }, [
    // Add hasRentalVariants to the dependency array
    hasRentalVariants,
    isErrorColumn,
    selectedWarehouses,
    product.postOrderShippingCost,
    // Remove product.variants from here since we're using memoized values
    availableCountriesPurchase,
    availableCountriesRent,
    attributes,
    classifications,
    selectedVendor,
    product.brand,
    product.productType,
  ]);

  const columns: any[] = parentHeaders.map((header) => header.columns).flat();

  const columnHeaders = columns.map(
    ({ key, label, keyId }, i) => {
      const sortProduct = (label: string, direction: string) => {
        let priceType = '';
        let sortField = label;
        if (keyId) {
          sortField = `attributeValues-${keyId}`;
        } else if (label.indexOf('pricePerUnit') > -1) {
          sortField = label.replace('pricePerUnit', 'productVariantPrices');
          priceType = PRICE_TYPE.PURCHASE;
        } else if (label.indexOf('rentalPrice') > -1) {
          const countryIso = label?.split?.('-')?.[1];
          const availableCountry = availableCountriesRent.find(x => x.countryIso === countryIso);
          sortField = `productVariantPrices-${availableCountry?.id}`;
          priceType = PRICE_TYPE.RENT;
        }

        setSortByHeader({ header: label, direction, sortField, priceType });
      };
      return (
        <Th orderIndex={i} key={i} className={i === 0 ? 'th-child' : ''}>
          <ThContents>
            {!KEYS_FOR_SORT.includes(key) && key.indexOf('range') === -1 && (
              <IconContainer>
                <SortingArrow
                  onClick={() => sortProduct(key, 'asc')}
                  direction="top"
                  margin="0px 0 3px 0"
                  color={
                    sortByHeader?.header === key &&
                      sortByHeader?.direction === 'asc'
                      ? Color.black
                      : Color.darkGray
                  }
                />
                <SortingArrow
                  onClick={() => sortProduct(key, 'desc')}
                  direction="bottom"
                  color={
                    sortByHeader?.header === key &&
                      sortByHeader?.direction === 'desc'
                      ? Color.black
                      : Color.darkGray
                  }
                />
              </IconContainer>
            )}
            {label}
          </ThContents>
        </Th>
      );
    }
  );

  const bodyRows = rows.map((row, i) => (
    <VariantTableRowV2
      key={i}
      columns={columns}
      errorInventoryTypes={row.errorInventoryTypes}
      errorTypes={row.errorTypes}
      stockFlag={stockFlag}
      focused={row.focused}
      focusedMode={focusedMode}
      onRowDelete={onRowDelete}
      onCellEdit={onCellEdit}
      showDeleteButton={i + 1 < rows.length}
      variant={row.variant}
      suggestionValuesList={suggestionValuesList}
      attributes={attributes}
      classifications={classifications}
      clearSuggestions={clearSuggestions}
      originalId={originalId}
      product={product}
      disabled={disabled}
      publishedSKUList={publishedSKUList}
      selectedWarehouses={selectedWarehouses}
      productStocks={productStocks}
      availableCountries={availableCountriesPurchase}
      isErrorColumn={isErrorColumn}
      selectedVariants={selectedVariants}
      type={type}
      onBlur={onBlur}
      commissionReadOnly={commissionReadOnly}
    />
  ));

  const parentColumns = (
    <Headers>
      <ThAction></ThAction>
      {parentHeaders.map((ph, index) =>
        ph.columns.length ? (
          <ThParent key={ph.key} colSpan={ph.colSpan} className={index === 0 ? 'th-parent' : index === parentHeaders.length - 1 ? 'th-parent-right' : ''}>
            <ThContentsParent className='heading'>{ph.name}</ThContentsParent>
          </ThParent>
        ) : null
      )}
      {/* <ThAction></ThAction> */}
    </Headers>
  );

  const childColumns = (
    <Headers>
      <ThAction>{TEXT_LABELS.ERRORS}</ThAction>
      {columnHeaders}
      {/* <ThAction>{TEXT_LABELS.ERRORS}</ThAction> */}
    </Headers>
  );

  return (
    <Container className="table-container-black" maxHeight={maxHeight}>
      <Suspense
        fallback={
          <LoadingIndicator margin="0 0 0 420px !important" size={35} />
        }
      >
        <BaseTable>
          <Thead>
            {parentColumns}
            {childColumns}
          </Thead>
          <tbody>{bodyRows}</tbody>
        </BaseTable>
      </Suspense>
    </Container>
  );
}
